import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';

type Squad = Database['public']['Tables']['squads']['Row'];
type SquadMember = Database['public']['Tables']['squad_members']['Row'];

export interface SquadWithMembers extends Squad {
  members: Array<SquadMember & { profile: { name: string; avatar_url: string | null } }>;
  member_count: number;
}

export function useSquads() {
  const [squads, setSquads] = useState<Squad[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSquads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSquads([]);
        return;
      }

      // Fetch squads where user is a member
      const { data: membersData, error: membersError } = await supabase
        .from('squad_members')
        .select('squad_id')
        .eq('user_id', user.id);

      if (membersError) throw membersError;

      if (!membersData || membersData.length === 0) {
        setSquads([]);
        return;
      }

      const squadIds = membersData.map(m => m.squad_id);

      const { data: squadsData, error: squadsError } = await supabase
        .from('squads')
        .select('*')
        .in('id', squadIds);

      if (squadsError) throw squadsError;

      setSquads((squadsData || []) as Squad[]);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch squads');
      setError(error);

    } finally {
      setLoading(false);
    }
  }, []);

  const createSquad = useCallback(async (name: string) => {
    try {
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('You must be logged in to create a squad');
      }

      // Create the squad (code will be auto-generated by trigger)
      const { data: squad, error: createError } = await supabase
        .from('squads')
        .insert({
          name,
          owner_id: user.id,
        })
        .select()
        .single();

      if (createError) throw createError;

      // Add creator as a member
      const { error: memberError } = await supabase
        .from('squad_members')
        .insert({
          squad_id: squad.id,
          user_id: user.id,
        });

      if (memberError) throw memberError;

      // Refresh squads list
      await fetchSquads();

      return { data: squad, error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create squad');
      setError(error);

      return { data: null, error };
    }
  }, [fetchSquads]);

  const joinSquad = useCallback(async (code: string) => {
    try {
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('You must be logged in to join a squad');
      }

      // Find squad by code
      const { data: squad, error: findError } = await supabase
        .from('squads')
        .select('id')
        .eq('code', code.toUpperCase())
        .single();

      if (findError || !squad) {
        throw new Error('Squad not found');
      }

      // Check if already a member
      const { data: existingMember } = await supabase
        .from('squad_members')
        .select('id')
        .eq('squad_id', squad.id)
        .eq('user_id', user.id)
        .single();

      if (existingMember) {
        throw new Error('You are already a member of this squad');
      }

      // Join the squad
      const { error: joinError } = await supabase
        .from('squad_members')
        .insert({
          squad_id: squad.id,
          user_id: user.id,
        });

      if (joinError) throw joinError;

      // Refresh squads list
      await fetchSquads();

      return { data: squad, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to join squad';
      const error = new Error(errorMessage);
      setError(error);

      return { data: null, error };
    }
  }, [fetchSquads]);

  const getSquadWithMembers = useCallback(async (squadId: string): Promise<SquadWithMembers | null> => {
    try {
      const { data, error } = await supabase
        .from('squads')
        .select(`
          *,
          squad_members (
            *,
            profile:profiles!squad_members_user_id_fkey (
              name,
              avatar_url
            )
          )
        `)
        .eq('id', squadId)
        .single();

      if (error) throw error;

      if (!data) return null;

      interface SquadMemberWithProfile extends SquadMember {
        profile: {
          name: string;
          avatar_url: string | null;
        };
      }
      const members = (data.squad_members as SquadMemberWithProfile[]).map((member) => ({
        ...member,
        profile: member.profile,
      }));

      return {
        ...data,
        members,
        member_count: members.length,
      } as SquadWithMembers;
    } catch (err) {
      console.error('Failed to get squad with members:', err);
      return null;
    }
  }, []);

  const getOwnedSquad = useCallback(async (): Promise<Squad | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return null;
      }

      // Get squad where user is the owner
      const { data: squad, error } = await supabase
        .from('squads')
        .select('*')
        .eq('owner_id', user.id)
        .maybeSingle();

      if (error) throw error;

      return squad as Squad | null;
    } catch (err) {
      console.error('Failed to get owned squad:', err);
      return null;
    }
  }, []);

  const renameSquad = useCallback(async (squadId: string, newName: string) => {
    try {
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('You must be logged in to rename a squad');
      }

      // Verify user owns the squad
      const { data: squad, error: checkError } = await supabase
        .from('squads')
        .select('owner_id')
        .eq('id', squadId)
        .single();

      if (checkError) throw checkError;

      if (!squad || squad.owner_id !== user.id) {
        throw new Error('You can only rename squads you own');
      }

      // Update the squad name
      const { data: updatedSquad, error: updateError } = await supabase
        .from('squads')
        .update({ name: newName.trim() })
        .eq('id', squadId)
        .select()
        .single();

      if (updateError) throw updateError;

      // Refresh squads list
      await fetchSquads();

      return { data: updatedSquad, error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to rename squad');
      setError(error);

      return { data: null, error };
    }
  }, [fetchSquads]);

  useEffect(() => {
    fetchSquads();
  }, [fetchSquads]);

  return {
    squads,
    loading,
    error,
    createSquad,
    joinSquad,
    getSquadWithMembers,
    getOwnedSquad,
    renameSquad,
    refetch: fetchSquads,
  };
}

